import { useCallback, useState } from 'react';
import {
  MenuItemLink,
  MenuProps as BaseMenuProps,
  ReduxState,
  useGetResourceLabel
} from 'react-admin';
import { useSelector } from 'react-redux';
import DefaultIcon from '@material-ui/icons/ViewList';

import * as resources from '../resources';
import Submenu from './Submenu';

type MenuProps = BaseMenuProps;

interface MenuState {
  menuBrands: boolean;
  menuUtilities: boolean;
  menuContact: boolean;
  menuPosts: boolean;
  menuVisitSchedules: boolean;
}

interface GetSubmenuProps {
  resource: keyof typeof resources;
  toggle: keyof MenuState;
  name?: string;
}

interface GetMenuItemLinkProps {
  resource: keyof typeof resources;
}

export default function Menu({ dense }: MenuProps) {
  const [state, setState] = useState<MenuState>({
    menuBrands: true,
    menuUtilities: true,
    menuContact: true,
    menuPosts: true,
    menuVisitSchedules: true
  });

  const getResourceLabel = useGetResourceLabel();

  const open = useSelector((state: ReduxState) => state.admin.ui.sidebarOpen);

  const handleToggle = useCallback((menu: keyof MenuState) => {
    setState(state => ({ ...state, [menu]: !state[menu] }));
  }, []);

  const getSubmenuProps = useCallback(
    ({ resource, toggle, name }: GetSubmenuProps) => {
      const Icon = resources[resource].icon;
      const resourceName = resources[resource].name;
      return {
        handleToggle: () => handleToggle(toggle),
        name: name || getResourceLabel(resourceName),
        icon: Icon ? <Icon /> : <DefaultIcon />,
        sidebarIsOpen: open,
        dense
      };
    },
    [dense, getResourceLabel, handleToggle, open]
  );

  const getMenuItemLinkProps = useCallback(
    ({ resource }: GetMenuItemLinkProps) => {
      const Icon = resources[resource].icon;
      const resourceName = resources[resource].name;
      return {
        to: `/${resourceName}`,
        primaryText: getResourceLabel(resourceName),
        leftIcon: Icon ? <Icon /> : <DefaultIcon />,
        sidebarIsOpen: open,
        dense
      };
    },
    [dense, getResourceLabel, open]
  );

  return (
    <div>
      <MenuItemLink {...getMenuItemLinkProps({ resource: 'slides' })} />

      

      <Submenu
        {...getSubmenuProps({
          resource: 'visitSchedules',
          toggle: 'menuVisitSchedules',
          name: 'Visita à Fábrica'
        })}
        isOpen={state.menuVisitSchedules}
      >
        <MenuItemLink {...getMenuItemLinkProps({ resource: 'visitSchedules' })} />
        <MenuItemLink {...getMenuItemLinkProps({ resource: 'visitBlockedDays' })} />
      </Submenu>

      <Submenu
        {...getSubmenuProps({
          resource: 'utilities',
          toggle: 'menuUtilities',
          name: 'Utilidades'
        })}
        isOpen={state.menuUtilities}
      >
        <MenuItemLink
          {...getMenuItemLinkProps({ resource: 'utilityCategories' })}
        />
        <MenuItemLink {...getMenuItemLinkProps({ resource: 'utilities' })} />
      </Submenu>

      <Submenu
        {...getSubmenuProps({
          resource: 'products',
          toggle: 'menuBrands',
          name: 'Catálogo de produtos'
        })}
        isOpen={state.menuBrands}
      >
        <MenuItemLink
          {...getMenuItemLinkProps({ resource: 'brandsCategories' })}
        />
        <MenuItemLink {...getMenuItemLinkProps({ resource: 'brands' })} />
        <MenuItemLink {...getMenuItemLinkProps({ resource: 'products' })} />
      </Submenu>

      <Submenu
        {...getSubmenuProps({
          resource: 'messages',
          toggle: 'menuContact',
          name: 'Contato'
        })}
        isOpen={state.menuContact}
      >
        <MenuItemLink {...getMenuItemLinkProps({ resource: 'messageSubjects' })} />
        <MenuItemLink {...getMenuItemLinkProps({ resource: 'messages' })} />
      </Submenu>

      <MenuItemLink {...getMenuItemLinkProps({ resource: 'offers' })} />
      
      <MenuItemLink {...getMenuItemLinkProps({ resource: 'pages' })} />

      <Submenu
        {...getSubmenuProps({
          resource: 'posts',
          toggle: 'menuPosts',
          name: 'Matérias'
        })}
        isOpen={state.menuPosts}
      >
        <MenuItemLink {...getMenuItemLinkProps({ resource: 'highlights' })} />
        <MenuItemLink {...getMenuItemLinkProps({ resource: 'posts' })} />
      </Submenu>

      <MenuItemLink {...getMenuItemLinkProps({ resource: 'awards' })} />

      <MenuItemLink {...getMenuItemLinkProps({ resource: 'certifications' })} />

      <MenuItemLink {...getMenuItemLinkProps({ resource: 'timelineItems' })} />

      <MenuItemLink
        {...getMenuItemLinkProps({ resource: 'responsibilityItems' })}
      />

      <MenuItemLink
        {...getMenuItemLinkProps({ resource: 'valorizationItems' })}
      />

      <MenuItemLink {...getMenuItemLinkProps({ resource: 'settings' })} />

      <MenuItemLink {...getMenuItemLinkProps({ resource: 'users' })} />
    </div>
  );
}
